import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { ButtonType } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import { Paths } from '@src/routes/paths';

export default function GlobalErrorHandler() {
  const error = useRouteError();
  console.error(error);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const { t } = useTranslation('view', { keyPrefix: 'error' });

  return (
    <div
      className="w-full h-full flex flex-col justify-center items-center bg-white space-y-2 p-4"
      data-testid="GlobalErrorHandlerComponent"
    >
      <h1 className="font-semibold text-xl mb-8">{t('apology')}</h1>
      <p className="pt-6 whitespace-pre-wrap text-sm">
        {t('details', getErrorDetails(error))}
      </p>
      <Link to={Paths.PROFILE}>
        <Button
          label={t('buttons.backToProfile')}
          buttonType={ButtonType.PRIMARY}
        />
      </Link>
      <Link to={Paths.LOGOUT}>
        <Button label={t('buttons.logout')} buttonType={ButtonType.SECONDARY} />
      </Link>
    </div>
  );
}

const getErrorDetails = (
  error: unknown,
): { errorMessage: string; stack: string } => {
  let errorMessage: string;
  const stack: string | undefined = (error as Error | undefined)?.stack;
  if (isRouteErrorResponse(error)) {
    errorMessage = `isRouteErrorResponse: ${error.status} ${
      error.statusText
    }\n${JSON.stringify(error.data)}`;
  } else {
    errorMessage = `unknown error ${JSON.stringify(error ?? 'undefined')}`;
  }
  return { errorMessage, stack: stack ?? '' };
};
